import { clsx } from 'clsx';
import { MouseEvent, ReactNode, useMemo } from 'react';

import { ToggleButton as ReactAriaToggleButton } from 'react-aria-components';

import { Icon, Link, LinkProps } from '@zapier/design-system';
import { NavItemKey } from './SecondaryNav';
import { useSecondaryNavContext } from './SecondaryNavContext';
import styles from './SecondaryNavItem.module.scss';
import { SecondaryNavItemContext } from './SecondaryNavItemContext';

type ItemProps = {
  /**
   * What to render in the `SecondaryNav.Item`.
   */
  children?: ReactNode;
  /**
   * Optional property for overriding styles.
   */
  className?: string;
  /**
   * unique identifier for this `SecondaryNav.Item`
   */
  itemKey: NavItemKey;
  /**
   * Content to display within the `SecondaryNav.Item`
   */
  label: ReactNode;
  /**
   * props for the `Link` component within `SecondaryNav.Item`.
   * `linkProps.children` is replaced by the `label` prop.
   */
  linkProps: Omit<LinkProps, 'children'>;
};

export const SecondaryNavItem = ({
  children,
  className,
  itemKey,
  label,
  linkProps,
}: ItemProps) => {
  const {
    selectedItemKey,
    expandedSectionKeys,
    registerSelectedItemKey,
    registerExpandedSectionKeys,
  } = useSecondaryNavContext();

  const isSelected = selectedItemKey === itemKey;
  const isExpanded = expandedSectionKeys.includes(itemKey);

  const onClick = (e: MouseEvent<Element, globalThis.MouseEvent>) => {
    registerSelectedItemKey(itemKey);

    linkProps.onClick?.(e);
  };

  const onToggle = () => {
    registerExpandedSectionKeys(
      isExpanded
        ? expandedSectionKeys.filter((k) => k !== itemKey)
        : [...expandedSectionKeys, itemKey]
    );
  };

  // Unique ID to associate the toggle button with the submenu it controls.
  const submenuId = `${itemKey}-menu`;

  const contextValue = useMemo(
    () => ({
      itemId: itemKey,
      listId: submenuId,
    }),
    [itemKey, submenuId]
  );

  return (
    <SecondaryNavItemContext.Provider value={contextValue}>
      <li className={clsx(styles.navItem, className)} data-zds>
        <div
          className={styles.navItemButton}
          data-selected={isSelected}
          data-zds
        >
          {/* TODO: Switch to beta `Link` component. This will a breaking change because the consuming app will need to be wrapped in a `DesignSystemProvider` and pass it a `navigate` function. */}
          <Link
            {...linkProps}
            aria-current={isSelected ? 'page' : undefined}
            className={clsx(styles.navItemLink, linkProps.className)}
            onClick={onClick}
          >
            {label}
          </Link>

          {children && (
            <ReactAriaToggleButton
              aria-controls={submenuId}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? 'Collapse menu' : 'Expand menu'}
              className={styles.navItemToggleButton}
              data-expanded={isExpanded}
              data-zds
              isSelected={isExpanded}
              onChange={onToggle}
            >
              <Icon name="arrowSmallDown" size={20} isBlock />
            </ReactAriaToggleButton>
          )}
        </div>

        {children}
      </li>
    </SecondaryNavItemContext.Provider>
  );
};
